/**
 * Catalog of Gemini image models the platform can render with.
 * Single source of truth for ids, display names and pricing used by both the
 * API (validation, cost accounting) and the web app (selectors, filters).
 */
export const IMAGE_MODELS = [
  {
    id: 'gemini-2.5-flash-image',
    displayName: 'Gemini 2.5 Flash',
    tier: 'Standard',
    baseCost: 0.03,
    selectable: true,
  },
  {
    id: 'gemini-3.1-flash-image',
    displayName: 'Gemini 3.1 Flash',
    tier: 'High-Res',
    baseCost: 0.06,
    selectable: true,
  },
  {
    id: 'gemini-3.1-flash-lite-image',
    displayName: 'Gemini 3.1 Flash Lite',
    tier: 'Lite',
    baseCost: 0.015,
    selectable: false,
  },
] as const;

export type ImageModel = (typeof IMAGE_MODELS)[number];
export type ImageModelId = ImageModel['id'];

export const IMAGE_MODEL_IDS = IMAGE_MODELS.map((m) => m.id) as [ImageModelId, ...ImageModelId[]];

export const DEFAULT_IMAGE_MODEL: ImageModelId = 'gemini-2.5-flash-image';

// Per-token surcharge on top of the model's base cost per render (USD).
const INPUT_TOKEN_RATE = 0.000000075;
const OUTPUT_TOKEN_RATE = 0.0000003;

export function getImageModel(modelId: string): ImageModel | undefined {
  return IMAGE_MODELS.find((m) => m.id === modelId);
}

export function getModelDisplayName(modelId: string): string {
  return getImageModel(modelId)?.displayName ?? modelId;
}

export function estimateGenerationCost(modelId: string, inputTokens = 0, outputTokens = 0): number {
  const baseRate = getImageModel(modelId)?.baseCost ?? IMAGE_MODELS[0].baseCost;
  const cost = baseRate + inputTokens * INPUT_TOKEN_RATE + outputTokens * OUTPUT_TOKEN_RATE;
  return Number(cost.toFixed(4));
}
